import { useEffect, useMemo, useState } from 'react';
import OrgModuleLayout from './OrgModuleLayout';
import { useConfig } from '@config/provider';
import { resolveBasePath } from '@app/utils/api';
import { buildAccessTokenProvider } from '@app/api/token';
import { useAuthSession } from '@app/context/auth-session';
import { createAuthFetch } from '@app/utils/auth-fetch';

type OpenApiParameter = {
  name?: string;
  in?: string;
  required?: boolean;
  description?: string;
};

type OpenApiOperation = {
  operationId?: string;
  summary?: string;
  description?: string;
  tags?: string[];
  parameters?: OpenApiParameter[];
  requestBody?: { required?: boolean; content?: Record<string, unknown> };
  responses?: Record<string, { description?: string }>;
};

type OpenApiDocument = {
  info?: { title?: string; version?: string };
  paths?: Record<string, Record<string, OpenApiOperation>>;
};

const HTTP_METHODS = ['get', 'post', 'put', 'patch', 'delete', 'options', 'head', 'trace'];

const formatMethod = (method: string) => method.toUpperCase();

const getOperations = (document: OpenApiDocument) =>
  Object.entries(document.paths ?? {}).flatMap(([path, pathItem]) =>
    Object.entries(pathItem)
      .filter(([method]) => HTTP_METHODS.includes(method))
      .map(([method, operation]) => ({ path, method, operation }))
  );

const resolveOpenApiUrl = (apiBaseUrl: string): string => {
  const basePath = resolveBasePath(apiBaseUrl);
  if (/^https?:\/\//i.test(basePath)) {
    const url = new URL(basePath);
    url.pathname = url.pathname.replace(/\/public\/v1$|\/v1$/, '/openapi.json');
    return url.toString();
  }

  return basePath.replace(/\/public\/v1$|\/v1$/, '/openapi.json');
};

const ApiDocsPage = () => {
  const { apiBaseUrl } = useConfig();
  const { refreshTokens } = useAuthSession();
  const openApiUrl = useMemo(() => resolveOpenApiUrl(apiBaseUrl), [apiBaseUrl]);
  const authFetch = useMemo(
    () => createAuthFetch({ resolveAccessToken: buildAccessTokenProvider(), refreshTokens }),
    [refreshTokens]
  );
  const [document, setDocument] = useState<OpenApiDocument | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setDocument(null);
    setError(null);

    authFetch
      .fetchJson<OpenApiDocument>(openApiUrl)
      .then((nextDocument) => {
        if (active) setDocument(nextDocument);
      })
      .catch((requestError: unknown) => {
        if (active) setError(requestError instanceof Error ? requestError.message : 'Unable to load the OpenAPI contract.');
      });

    return () => {
      active = false;
    };
  }, [authFetch, openApiUrl]);

  const operations = useMemo(() => getOperations(document ?? {}), [document]);
  const groupedOperations = useMemo(() => {
    const groups = new Map<string, typeof operations>();
    operations.forEach((entry) => {
      const tags = entry.operation.tags?.length ? entry.operation.tags : ['Other endpoints'];
      tags.forEach((tag) => groups.set(tag, [...(groups.get(tag) ?? []), entry]));
    });
    return [...groups.entries()].sort(([first], [second]) => first.localeCompare(second));
  }, [operations]);

  return (
    <OrgModuleLayout
      title="API Documentation"
      description="Browse every endpoint exposed by the configured Reap public API."
      scrollMode="all">
      <div className="api-docs-page">
        <section className="api-docs-page__hero">
          <div>
            <span className="ui-badge ui-badge--success">Live contract</span>
            <h2>Reap public API</h2>
            <p>
              This catalog is loaded directly from the configured API environment, so it stays aligned with the available endpoints.
            </p>
          </div>
          <a className="ui-button ui-button--primary" href={openApiUrl} target="_blank" rel="noreferrer">
            Open OpenAPI JSON
          </a>
        </section>

        <section className="api-docs-page__section">
          <div className="api-docs-page__section-heading">
            <div>
              <h2>All API endpoints</h2>
              <p>{document ? `${operations.length} operations available` : 'Loading the live API contract…'}</p>
            </div>
            {document?.info?.version && <code>v{document.info.version}</code>}
          </div>

          {error && (
            <div className="api-docs-page__state api-docs-page__state--error">
              <strong>Could not load the API catalog</strong>
              <span>{error} Open the raw contract to inspect it directly.</span>
            </div>
          )}

          {!error && !document && <div className="api-docs-page__state">Loading endpoints…</div>}

          {document && groupedOperations.map(([tag, entries]) => (
            <section className="api-docs-page__endpoint-group" key={tag}>
              <h3>{tag}</h3>
              <div className="api-docs-page__endpoints">
                {entries.map(({ method, path, operation }) => (
                  <details className="api-docs-page__endpoint" key={`${method}:${path}`}>
                    <summary>
                      <span className={`api-docs-page__method api-docs-page__method--${method}`}>{formatMethod(method)}</span>
                      <code>{path}</code>
                      <span className="api-docs-page__endpoint-summary">{operation.summary || operation.operationId || 'API operation'}</span>
                    </summary>
                    <div className="api-docs-page__endpoint-details">
                      {operation.description && <p>{operation.description}</p>}
                      {operation.operationId && <code>operationId: {operation.operationId}</code>}
                      {!!operation.parameters?.length && (
                        <div>
                          <strong>Parameters</strong>
                          <ul>
                            {operation.parameters.map((parameter) => (
                              <li key={`${parameter.in}:${parameter.name}`}>
                                <code>{parameter.name}</code> <span>{parameter.in}</span>{parameter.required ? ' · required' : ''}
                                {parameter.description && ` — ${parameter.description}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {operation.requestBody && (
                        <div>
                          <strong>Request body</strong>
                          <span>{operation.requestBody.required ? 'Required' : 'Optional'} · {Object.keys(operation.requestBody.content ?? {}).join(', ') || 'JSON'}</span>
                        </div>
                      )}
                      {!!Object.keys(operation.responses ?? {}).length && (
                        <div>
                          <strong>Responses</strong>
                          <ul>
                            {Object.entries(operation.responses ?? {}).map(([status, response]) => (
                              <li key={status}><code>{status}</code>{response.description ? ` — ${response.description}` : ''}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          ))}
        </section>

        <section className="api-docs-page__section">
          <h2>Integration resources</h2>
          <div className="api-docs-page__cards">
            <a className="api-docs-page__card" href={openApiUrl} target="_blank" rel="noreferrer">
              <strong>OpenAPI contract</strong>
              <span>Download or inspect the current machine-readable API schema.</span>
              <code>{openApiUrl}</code>
            </a>
            <div className="api-docs-page__card">
              <strong>Generated SDK</strong>
              <span>Use the generated TypeScript SDK when working with supported Reap API operations.</span>
              <code>@reap/openapi-public-v1</code>
            </div>
          </div>
        </section>
      </div>
    </OrgModuleLayout>
  );
};

export default ApiDocsPage;
